import { Container, Graphics, type Sprite } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import { PUDDLE_STRETCH } from '../../sim/puddles';
import type { PuddleState, SimState } from '../../sim/state';
import type { RenderContext } from '../context';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import { add, setTint } from '../util';
import { ViewPool } from '../views';
import {
  ARCS,
  PUDDLE,
  RINGS,
  SPARKS,
  arcPoints,
  blobRadii,
  puddleSize,
  spark,
  splashDrop,
  waterRing,
} from './orage-moments';

const WATER = { fill: 0x080b1e, rim: 0xc9d2f5, rimAlpha: 0.5, rimWidth: 3 };
// Every arc gets its own `moveTo`: Pixi would run a line to it from where the last one ended.
const CRESCENT = { alpha: 0.5, width: 2, from: Math.PI * 1.05, to: Math.PI * 1.75 };
const STAGE = { color: 0x9ff5c8, size: 0.42, alpha: 0.5 };
const RING = { from: 2, grow: 9, alpha: 0.7, width: 1.1 };
const SPLASH = 1.8;
const ARC = { color: 0xb8eaff, alpha: 0.85, width: 1.4 };
const SPARK = 1.6;

interface Puddle {
  seed: number;
  radii: number[];
  readonly glow: Sprite;
}

interface Sized {
  readonly puddle: PuddleState;
  readonly radius: number;
}

export interface Puddles {
  update(state: SimState, alpha: number, frame: Frame): void;
  reset(): void;
  destroy(): void;
}

export function createPuddles(ctx: RenderContext): Puddles {
  const { layers, textures, options } = ctx;
  const water = layers.decor.addChild(new Graphics());
  const glows = layers.glow.addChild(new Container());
  const light = layers.fx.addChild(new Graphics());
  const views = new ViewPool<Puddle>(
    () => ({ seed: Number.NaN, radii: [], glow: add(glows, textures.halo) }),
    (view) => {
      view.glow.visible = false;
    },
  );

  function outline(sized: Sized, radii: readonly number[]): void {
    const { puddle, radius } = sized;
    const points = radii.map((share, index): [number, number] => {
      const angle = (index / radii.length) * TAU;
      return [
        puddle.x + Math.cos(angle) * radius * share * PUDDLE_STRETCH.x,
        puddle.y + Math.sin(angle) * radius * share * PUDDLE_STRETCH.y,
      ];
    });
    const mids = points.map(([x, y], index): [number, number] => {
      const [nx, ny] = points[(index + 1) % points.length] ?? [x, y];
      return [(x + nx) / 2, (y + ny) / 2];
    });
    const [sx, sy] = mids[0] ?? [puddle.x, puddle.y];
    water.moveTo(sx, sy);
    points.forEach(([x, y], index) => {
      const [mx, my] = mids[index] ?? [x, y];
      if (index > 0) {
        water.quadraticCurveTo(x, y, mx, my);
      }
    });
    const [fx, fy] = points[0] ?? [puddle.x, puddle.y];
    water.quadraticCurveTo(fx, fy, sx, sy);
    water.closePath();
  }

  function reflections(sized: Sized, view: Puddle, beats: number, frame: Frame): void {
    const { puddle, radius } = sized;
    const { x, y } = puddle;
    const cx = x - radius * 0.35;
    const cy = y - radius * 0.28;
    const cr = radius * 0.55;
    water
      .moveTo(cx + Math.cos(CRESCENT.from) * cr, cy + Math.sin(CRESCENT.from) * cr)
      .arc(cx, cy, cr, CRESCENT.from, CRESCENT.to)
      .stroke({ width: CRESCENT.width, color: WATER.rim, alpha: CRESCENT.alpha });
    view.glow.visible = true;
    setTint(view.glow, STAGE.color);
    view.glow.position.set(x + radius * 0.45, y + radius * 0.05);
    view.glow.scale.set(
      (STAGE.size * radius) / textures.halo.radius,
      (STAGE.size * radius * 0.5) / textures.halo.radius,
    );
    view.glow.alpha = STAGE.alpha * frame.light.haloAlpha;
    for (let index = 0; index < RINGS; index += 1) {
      const ring = waterRing(puddle.id, index, beats);
      const alpha = RING.alpha * (1 - ring.age);
      if (alpha <= 0) {
        continue;
      }
      water
        .circle(x + ring.x * radius, y + ring.y * radius, RING.from + ring.age * RING.grow)
        .stroke({ width: RING.width, color: WATER.rim, alpha });
    }
  }

  function splash(sized: Sized, age: number, rim: number): void {
    const { puddle, radius } = sized;
    for (let index = 0; index < PUDDLE.splashes; index += 1) {
      const drop = splashDrop(index, age);
      if (drop !== null) {
        light
          .circle(puddle.x + drop.x * radius, puddle.y + drop.y * radius, SPLASH)
          .fill({ color: rim, alpha: drop.alpha });
      }
    }
  }

  function sparks(sized: Sized, beats: number, color: number): void {
    const { puddle, radius } = sized;
    for (let index = 0; index < SPARKS; index += 1) {
      const spot = spark(puddle.id, index, beats);
      light
        .circle(puddle.x + spot.x * radius, puddle.y + spot.y * radius, SPARK)
        .fill({ color, alpha: spot.alpha });
    }
  }

  function arcs(state: SimState, alpha: number, beats: number): void {
    for (const trap of state.traps) {
      if ((trap.shortedTicks ?? 0) <= 0) {
        continue;
      }
      const x = lerp(trap.prevX, trap.x, alpha);
      const y = lerp(trap.prevY, trap.y, alpha);
      for (let index = 0; index < ARCS.count; index += 1) {
        arcPoints(trap.id, index, beats, options.calmMode).forEach(([dx, dy], point) => {
          if (point === 0) {
            light.moveTo(x + dx, y + dy);
          } else {
            light.lineTo(x + dx, y + dy);
          }
        });
        light.stroke({ width: ARC.width, color: ARC.color, alpha: ARC.alpha, cap: 'round' });
      }
    }
  }

  return {
    update(state, alpha, frame): void {
      water.clear();
      light.clear();
      views.begin();
      const beat = frame.tempo.ticksPerBeat;
      const beats = frame.now / beat;
      const sized = (state.puddles ?? [])
        .map((puddle): Sized => ({
          puddle,
          radius:
            puddle.radius *
            puddleSize((frame.now - puddle.bornTick) / beat, (puddle.driesTick - frame.now) / beat),
        }))
        .filter((item) => item.radius >= 1);
      for (const item of sized) {
        const view = views.acquire(item.puddle.id);
        if (view.seed !== item.puddle.id) {
          view.seed = item.puddle.id;
          view.radii = blobRadii(item.puddle.id);
        }
        outline(item, view.radii);
      }
      if (sized.length > 0) {
        water
          .stroke({ width: WATER.rimWidth, color: WATER.rim, alpha: WATER.rimAlpha, join: 'round' })
          .fill({ color: WATER.fill });
      }
      for (const item of sized) {
        const view = views.acquire(item.puddle.id);
        reflections(item, view, beats, frame);
        splash(item, (frame.now - item.puddle.bornTick) / beat, WATER.rim);
        if (!state.enemies.some((enemy) => enemy.id === item.puddle.byId)) {
          sparks(item, beats, frame.palette.noyau);
        }
      }
      arcs(state, alpha, beats);
      views.end();
    },
    reset(): void {
      views.releaseAll();
      water.clear();
      light.clear();
    },
    destroy(): void {
      for (const container of [water, glows, light]) {
        container.destroy({ children: true });
      }
    },
  };
}
